/**
 * Meta tools — final_answer terminator, navigate_to, and highlight_element.
 */

const BACKSLASH_SENTINEL = "\uE000";

/**
 * Decode the escape sequences a model might leave literally encoded inside
 * a JSON tool-arg string. Different adapters double-escape: Grok in
 * particular has shipped responses where `arguments.text` ends up as
 * `"Line1\\nLine2"` (literal backslash + n) instead of `"Line1\nLine2"`
 * (real newline), so the user sees `\n` in the rendered reply. We decode
 * here at the final-answer boundary so the markdown renderer always
 * receives real whitespace, regardless of which model double-escaped.
 *
 * Properly-encoded text (already containing real newlines) is unchanged —
 * the regex only matches the literal sequence backslash+letter, never a
 * standalone control character.
 */
export function decodeStringEscapes(s) {
  if (typeof s !== "string" || !s) return s;
  // Sentinel-protect real backslashes so `\\n` in source (which means
  // "literal backslash followed by n") survives intact.
  return s
    .replace(/\\\\/g, BACKSLASH_SENTINEL)
    .replace(/\\n/g, "\n")
    .replace(/\\r/g, "\r")
    .replace(/\\t/g, "\t")
    .replace(/\\"/g, '"')
    .replace(/\\'/g, "'")
    .replaceAll(BACKSLASH_SENTINEL, "\\");
}

export const final_answer = {
  name: "final_answer",
  description:
    "End your turn with a user-visible reply. EVERY turn must end by calling this. " +
    "Markdown in `text` is rendered. Be concise.",
  parameters: {
    type: "object",
    properties: { text: { type: "string" } },
    required: ["text"],
    additionalProperties: false,
  },
  execute: async ({ text }) => ({
    ok: true,
    data: { text: decodeStringEscapes(text || "") },
  }),
};

export const navigate_to = {
  name: "navigate_to",
  description:
    "Switch the gateway's active section. Use when the user asks to go somewhere (e.g. 'open MQTT brokers'). " +
    "For `plc-runtime-fleet`, pass `view: \"hosts\"` or `view: \"controllers\"` when the requested object is known. " +
    "Section ids must match the gateway's routes — common ones: " +
    "`platform-overview`, `accounts-settings`, `user-management`, `projects-list`, " +
    "`plc-overview`, `plc-runtime-fleet`, `plc-deployments`, `plc-protocol-services`, `opcua-server`, `s7comm-server`, `plc-compatibility`, " +
    "`database-connections`, `api-connections`, `api-credentials`, `oauth-apps`, " +
    "`mqtt-brokers`, `historian-providers`, `api-ai-settings`, `alarms-management`, `alarms-notifications`. " +
    "If you pass an unknown id, the gateway falls back to platform-overview — pick from this list.",
  parameters: {
    type: "object",
    properties: {
      sectionId: { type: "string", minLength: 1 },
      view: {
        type: "string",
        enum: ["hosts", "controllers"],
        description: "Optional Runtime Fleet tab; valid only with plc-runtime-fleet.",
      },
    },
    required: ["sectionId"],
    additionalProperties: false,
  },
  execute: async ({ sectionId, view }, ctx) => {
    if (typeof ctx?.navigate !== "function") {
      return { ok: false, error: { code: "NO_NAVIGATE", message: "Navigation not available." } };
    }
    try {
      ctx.navigate(sectionId, view ? { view } : undefined);
      return { ok: true, data: { navigated: sectionId, ...(view ? { view } : {}) } };
    } catch (err) {
      return {
        ok: false,
        error: { code: "NAVIGATE_FAILED", message: err?.message || "Navigation failed" },
      };
    }
  },
};

export const highlight_element = {
  name: "highlight_element",
  description:
    "Show users where something is on the screen by drawing an animated highlight box around it. " +
    "Prefer this over `navigate_to` when the user asks 'where is X?' — point them at the right " +
    "thing without yanking them off their current page. " +
    "Pass a CSS selector that matches the on-screen element. If the target lives on a DIFFERENT " +
    "section, ALSO pass `sectionId` so the assistant navigates first.\n\n" +
    "## Selector cookbook\n" +
    "Always-present (any page):\n" +
    "  - `.gwAIButton` — the AI button (this assistant's own toggle)\n" +
    "  - `.gwProfileButton`, `.gwProfileWrap` — profile avatar\n" +
    "  - `.gwSearchWrap` — top search bar\n" +
    "  - `.gwNav` — left navigation panel\n" +
    "  - `.gwMain` — current page content area\n" +
    "  - `[data-gw-section=\"<sectionId>\"]` — main content of a specific section\n" +
    "  - `[data-gw-nav-item=\"<sectionId>\"]` — the LEFT-NAV item that opens a section. " +
    "When the user asks 'where is the X page?' use this — it points at the menu without leaving.\n" +
    "Per-section (when that page is loaded):\n" +
    "  - `.gwPageHeader` — page title/subtitle row\n" +
    "  - `.gwPrimaryBtn` — primary action button (Add / New / Connect)\n" +
    "  - `.gwCardSection` — a list card; multiple may exist on one page\n" +
    "  - `[data-gw-card=\"<name>\"]` — a SPECIFIC card by name. Known cards: " +
    "`ai_providers`, `ai_preferences` (AI Settings); " +
    "`workspace_routing`, `workspace_sidebar`, `workspace_access` (Gateway Settings); " +
    "`plc_overview`, `plc_overview_attention`, `plc_overview_coverage` (PLC Overview); " +
    "`plc_edge_enroll`, `plc_edge_runtimes`, `plc_controllers`, `plc_controller_truth`, `plc_controller_lifecycle_proposal` (Runtime Fleet); " +
    "`plc_fleet_create`, `plc_fleet_rollouts` (Deployments); " +
    "`overview_stats`, `cpu_trend`, `memory_trend`, `resource_gauges`, `system_info` (Platform Overview).\n" +
    "  - `[data-gw-stat=\"cpu|memory|disk|uptime\"]` — individual stat card on Platform Overview. " +
    "Use this for 'highlight my CPU' / 'show me memory'.\n" +
    "Specific resource ROW (for 'highlight my xAI provider' / 'show me the postgres connection'):\n" +
    "  - `[data-gw-resource=\"<type>\"][data-gw-id=\"<id>\"]` — match by id. " +
    "Get the id from the matching `list_*` tool first; never invent an id.\n" +
    "  - `[data-gw-resource=\"<type>\"][data-gw-name=\"<name>\"]` — match by display name. " +
    "Useful for datasources (keyed by name) and when the user mentions the resource by name.\n" +
    "Resource types match the dispatcher: `ai_providers`, `datasources`, `api_profiles`, " +
    "`credentials`, `oauth_apps`, `mqtt_brokers`, `mqtt_subscriptions`, `historian_providers`, `users`, `invites`, " +
    "`groups`, `projects`, `edge_gateways`, `plc_controllers`. The corresponding section ids: `api-ai-settings`, `database-connections`, " +
    "`api-connections`, `api-credentials`, `oauth-apps`, `mqtt-brokers`, `mqtt-brokers`, " +
    "`historian-providers`, `user-management`, `user-management`, `user-management`, `projects-list`, `plc-edge-runtimes`, `plc-controllers`. " +
    "The two legacy PLC ids resolve to the matching Runtime Fleet tab and remain valid for exact row highlighting.\n" +
    "  - `[data-gw-action=\"hardware-io\"]` — open Hardware I/O for an edge runtime row.\n\n" +
    "**Pattern for 'where is my X?'**:\n" +
    "  1. Call `list_<resource>` to look up the real id/name.\n" +
    "  2. If the resource lives on the active section, just highlight the row selector.\n" +
    "  3. If on a different section, pass `sectionId` AND the row selector — the tool navigates, " +
    "waits for the row to mount, then highlights. Don't call `navigate_to` first.",
  parameters: {
    type: "object",
    properties: {
      selector: { type: "string", minLength: 1 },
      sectionId: { type: "string" },
      label: { type: "string" },
      durationMs: { type: "integer" },
    },
    required: ["selector"],
    additionalProperties: false,
  },
  execute: async ({ selector, sectionId, label, durationMs }, ctx) => {
    if (typeof window === "undefined") {
      return {
        ok: false,
        error: { code: "NO_WINDOW", message: "Cannot highlight in non-browser context." },
      };
    }
    const navigated = sectionId && typeof ctx?.navigate === "function" && ctx.activeSection !== sectionId;
    if (navigated) {
      try {
        ctx.navigate(sectionId);
      } catch (err) {
        return {
          ok: false,
          error: {
            code: "NAVIGATE_FAILED",
            message: err?.message || `Cannot navigate to "${sectionId}".`,
          },
        };
      }
    }
    // Poll for the selector — the new section may not have mounted yet,
    // and React's useEffect-driven data fetches can delay first paint.
    // Without polling, a "highlight" right after a navigate would race the
    // mount and miss. We give it up to ~1.2s before reporting not-found.
    const tryQuery = () => {
      try {
        return document.querySelector(selector);
      } catch {
        return null;
      }
    };
    let element = tryQuery();
    if (!element) {
      const deadline = Date.now() + (navigated ? 1200 : 250);
      while (!element && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 60));
        element = tryQuery();
      }
    }
    if (!element) {
      return {
        ok: false,
        error: {
          code: "SELECTOR_NOT_FOUND",
          message: `No element matched "${selector}"${navigated ? ` after navigating to "${sectionId}"` : ""}. Try a different selector, or pass sectionId so the assistant navigates first.`,
        },
      };
    }
    try {
      window.dispatchEvent(
        new CustomEvent("gw-ai-highlight", {
          detail: { selector, label, durationMs },
        })
      );
    } catch {}
    return {
      ok: true,
      data: {
        highlighted: selector,
        sectionId: sectionId || ctx?.activeSection || null,
        label: label || null,
      },
    };
  },
};

const metaTools = [final_answer, navigate_to, highlight_element];

export default metaTools;
